import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { authorizedFetch } from '@/lib/api'

export type AccessRequestStatus =
  | 'pending'
  | 'invited'
  | 'session_sent'
  | 'converted'
  | 'approved'
  | 'rejected'

export interface AccessRequest {
  id: string
  email: string
  name: string
  source: 'waitlist_form' | 'registration'
  status: AccessRequestStatus
  created_at: string
  reviewed_at?: string | null
  reviewed_by_email?: string | null
  invitation_key?: string | null
  invitation_accepted?: boolean | null
  note?: string | null
  company?: string | null
  role?: string | null
  request_type?: string | null
  /** Plugins add row keys through the API `access_request_fields` hook. */
  [key: string]: unknown
}

export interface AccessRequestListResponse {
  items: AccessRequest[]
  counts: Partial<Record<AccessRequestStatus, number>>
}

export interface AccessRequestsQueryParams {
  status?: AccessRequestStatus | ''
  search?: string
  limit?: number
}

export function useAccessRequests(params?: AccessRequestsQueryParams) {
  return useQuery({
    queryKey: ['access-requests', params],
    queryFn: async (): Promise<AccessRequestListResponse> => {
      const searchParams = new URLSearchParams()
      if (params?.status) searchParams.append('status', params.status)
      if (params?.search) searchParams.append('search', params.search)
      if (params?.limit) searchParams.append('limit', params.limit.toString())

      const response = await authorizedFetch(`/api/accounts/waitlist/admin?${searchParams.toString()}`)
      if (!response.ok) {
        throw new Error('Failed to fetch access requests')
      }
      return response.json()
    },
    staleTime: 30 * 1000,
  })
}

export function useInviteAccessRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (entryId: string): Promise<AccessRequest> => {
      const response = await authorizedFetch(`/api/accounts/waitlist/admin/${entryId}/invite`, {
        method: 'POST',
      })
      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.error || 'Failed to invite')
      }
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access-requests'] })
    },
  })
}

export function useApproveAccessRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (entryId: string): Promise<AccessRequest> => {
      const response = await authorizedFetch(`/api/accounts/waitlist/admin/${entryId}/approve`, {
        method: 'POST',
      })
      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.error || 'Failed to approve')
      }
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access-requests'] })
    },
  })
}

export function useRejectAccessRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (entryId: string): Promise<AccessRequest> => {
      const response = await authorizedFetch(`/api/accounts/waitlist/admin/${entryId}/reject`, {
        method: 'POST',
      })
      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.error || 'Failed to reject')
      }
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access-requests'] })
    },
  })
}

export function useBulkInviteAccessRequests() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (ids: string[]): Promise<{ invited: string[]; errors: Record<string, string> }> => {
      const response = await authorizedFetch('/api/accounts/waitlist/admin/bulk-invite', {
        method: 'POST',
        body: JSON.stringify({ ids }),
      })
      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.error || 'Failed to invite')
      }
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access-requests'] })
    },
  })
}
